import { readPublicConfig } from "../config";
import { type ProposalPriceCatalog, resolveProposalPrices } from "./proposalPriceModel";

export type AppointmentProposal = {
  source: "live";
  token: string;
  recipientName?: string;
  message?: string;
  imageUrl?: string | null;
  offerTitle: string;
  variantLabel?: string;
  durationMinutes?: number;
  priceCents?: number | null;
  standardPriceCents?: number | null;
  personalPriceCents?: number | null;
  currency: string;
  locationName?: string;
  addressSummary?: string;
  expiresAt?: string | null;
  options: Array<{
    id: string;
    slotId: string;
    startsAt: string;
    endsAt: string;
    timezone: string;
  }>;
};

export type ProposalContact = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  note?: string;
  consent: boolean;
};

function functionApiUrl(name: string): string {
  const config = readPublicConfig(import.meta.env);
  return `${config.bookingApiUrl.replace(/\/booking-api$/, "")}/${name}`;
}

const VISUAL_FIXTURE_TOKEN = "atelier-visual";

function visualFixtureProposal(): AppointmentProposal {
  return {
    source: "live",
    token: VISUAL_FIXTURE_TOKEN,
    recipientName: "Daniela Beispiel",
    message: "Hier ist dein persönlicher Vorschlag. Wenn alles passt, bestätige ihn direkt.",
    imageUrl: null,
    offerTitle: "Persönliches Gespräch",
    variantLabel: "90 Minuten",
    durationMinutes: 90,
    priceCents: 24000,
    standardPriceCents: 28000,
    personalPriceCents: 24000,
    currency: "EUR",
    locationName: "Online · Google Meet",
    addressSummary: "Persönlicher Videocall",
    expiresAt: null,
    options: [
      {
        id: "opt-1",
        slotId: "slot-1",
        startsAt: "2026-11-10T08:30:00.000Z",
        endsAt: "2026-11-10T10:00:00.000Z",
        timezone: "Europe/Berlin",
      },
      {
        id: "opt-2",
        slotId: "slot-2",
        startsAt: "2026-11-12T13:00:00.000Z",
        endsAt: "2026-11-12T14:30:00.000Z",
        timezone: "Europe/Berlin",
      },
    ],
  };
}

export async function loadAppointmentProposal(token: string): Promise<AppointmentProposal> {
  if (import.meta.env.DEV && token === VISUAL_FIXTURE_TOKEN) {
    return visualFixtureProposal();
  }

  const config = readPublicConfig(import.meta.env);
  const [proposalResponse, catalogResponse] = await Promise.all([
    fetch(`${functionApiUrl("appointment-proposals")}?token=${encodeURIComponent(token)}`, {
      headers: { Accept: "application/json" },
    }),
    fetch(config.bookingApiUrl, { headers: { Accept: "application/json" } }).catch(() => null),
  ]);

  const payload = (await proposalResponse.json()) as AppointmentProposal & { error?: string };
  if (!proposalResponse.ok || payload.source !== "live") {
    throw new Error(proposalError(payload.error));
  }

  let catalog: ProposalPriceCatalog = {};
  if (catalogResponse?.ok) {
    catalog = (await catalogResponse.json().catch(() => ({}))) as ProposalPriceCatalog;
  }

  return {
    ...payload,
    ...resolveProposalPrices(payload, catalog),
  };
}

export async function bookAppointmentProposal(
  token: string,
  optionId: string,
  contact: ProposalContact,
): Promise<{ reference: string; managementToken: string }> {
  const response = await fetch(functionApiUrl("book-appointment-proposal"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, optionId, contact }),
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(proposalError(payload.error));
  return payload;
}

function proposalError(error?: string): string {
  if (error?.includes("expired")) return "Diese Terminauswahl ist inzwischen abgelaufen.";
  if (error?.includes("booked")) return "Aus dieser Auswahl wurde bereits ein Termin gebucht.";
  if (error?.includes("revoked")) return "Diese Terminauswahl ist nicht mehr verfügbar.";
  if (error?.includes("not_found")) {
    return "Diese persönliche Terminauswahl wurde nicht gefunden.";
  }
  if (error?.includes("calendar_conflict")) {
    return "Dieser Termin ist inzwischen belegt. Bitte wähle einen anderen.";
  }
  return "Die persönliche Terminauswahl konnte nicht geladen oder gebucht werden.";
}
